import { Alert, Button, Input, Modal, notification, Popconfirm, Select, Space, Switch, Table, Tag, Tooltip } from "antd";
import { BarChartOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import locale from "antd/es/date-picker/locale/es_ES";
import { AntdDatePicker, InternalPageLayout } from "components";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import useQuizAccess from "./useQuizAccess";
import { countAttempts, deleteQuiz, Quiz, QuizStatus, setQuizStatus, statusLabels, subscribeQuizzes } from "./quizModel";

const { RangePicker } = AntdDatePicker;

const QuizManager = () => {
    const navigate = useNavigate();
    const { canManage } = useQuizAccess();
    const [quizzes, setQuizzes] = useState<Quiz[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState<QuizStatus | "all">("all");
    const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    useEffect(() => subscribeQuizzes(
        (data) => {
            setQuizzes(data);
            setIsLoading(false);
        },
        (error) => {
            console.error("Quizzes snapshot failed", error);
            notification.error({ message: "Error", description: "No se pudieron cargar los cuestionarios" });
            setIsLoading(false);
        }
    ), []);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return quizzes.filter((q) => {
            if (status !== "all" && q.status !== status) return false;
            if (term && !q.title.toLowerCase().includes(term)) return false;
            if (range?.[0] && (!q.quizDate || dayjs(q.quizDate).isBefore(range[0], "day"))) return false;
            if (range?.[1] && (!q.quizDate || dayjs(q.quizDate).isAfter(range[1], "day"))) return false;
            return true;
        });
    }, [quizzes, search, status, range]);

    const changeStatus = async (quiz: Quiz, active: boolean) => {
        const apply = async () => {
            setBusyId(quiz.id);
            try {
                await setQuizStatus(quiz.id, active ? "active" : "inactive");
                notification.success({ message: active ? "Cuestionario activado" : "Cuestionario desactivado" });
            } catch (error) {
                console.error(error);
                notification.error({ message: "Error", description: "No se pudo cambiar el estado" });
            }
            setBusyId(null);
        };
        const otherActive = quizzes.find((q) => q.status === "active" && q.id !== quiz.id);
        if (active && otherActive) {
            Modal.confirm({
                title: "Activar cuestionario",
                content: `"${otherActive.title}" está activo y se desactivará. La web solo muestra un cuestionario a la vez.`,
                okText: "Activar",
                cancelText: "Cancelar",
                onOk: apply
            });
        } else {
            await apply();
        }
    };

    const remove = async (quiz: Quiz) => {
        setBusyId(quiz.id);
        try {
            const attempts = await countAttempts(quiz.id);
            if (attempts > 0) {
                notification.warning({
                    message: "No se puede eliminar",
                    description: `El cuestionario tiene ${attempts} intento(s) registrados. Desactívalo para conservar sus resultados.`
                });
            } else {
                await deleteQuiz(quiz.id);
                notification.success({ message: "Cuestionario eliminado" });
            }
        } catch (error) {
            console.error(error);
            notification.error({ message: "Error", description: "No se pudo eliminar el cuestionario" });
        }
        setBusyId(null);
    };

    const columns: ColumnsType<Quiz> = [
        {
            title: "Fecha",
            dataIndex: "quizDate",
            sorter: (a, b) => (a.quizDate?.getTime() ?? 0) - (b.quizDate?.getTime() ?? 0),
            render: (date: Date | null) => {
                if (!date) return "—";
                const d = dayjs(date);
                return (
                    <Tooltip title={d.day() !== 2 ? "No cae en martes" : undefined}>
                        <span style={{ color: d.day() !== 2 ? "#D46B08" : undefined }}>{d.format("DD/MM/YYYY")}</span>
                    </Tooltip>
                );
            }
        },
        {
            title: "Cuestionario",
            dataIndex: "title",
            render: (title: string, q) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{title}</div>
                    {q.description && <div style={{ fontSize: 12, color: "#8C8C8C" }}>{q.description}</div>}
                </div>
            )
        },
        { title: "Preguntas", dataIndex: "questionCount", width: 100 },
        {
            title: "Estado",
            dataIndex: "status",
            width: 110,
            render: (s: QuizStatus) => <Tag color={statusLabels[s].color}>{statusLabels[s].label}</Tag>
        },
        {
            title: "Activo",
            key: "active",
            width: 90,
            render: (_, q) => (
                <Switch
                    checked={q.status === "active"}
                    disabled={!canManage}
                    loading={busyId === q.id}
                    onChange={(checked) => changeStatus(q, checked)}
                />
            )
        },
        {
            title: "Actualizado",
            dataIndex: "updatedAt",
            render: (d: Date | null) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "—")
        },
        {
            title: "",
            key: "actions",
            width: 140,
            render: (_, q) => (
                <Space>
                    <Tooltip title="Ver resultados">
                        <Button size="small" icon={<BarChartOutlined />} onClick={() => navigate(`/quiz?quizId=${q.id}`)} />
                    </Tooltip>
                    {canManage && (
                        <>
                            <Tooltip title="Editar">
                                <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/quiz/manage/${q.id}`)} />
                            </Tooltip>
                            <Popconfirm title="¿Eliminar este cuestionario?" okText="Eliminar" cancelText="Cancelar" onConfirm={() => remove(q)}>
                                <Button size="small" danger icon={<DeleteOutlined />} disabled={busyId === q.id} />
                            </Popconfirm>
                        </>
                    )}
                </Space>
            )
        }
    ];

    return (
        <InternalPageLayout title="Cuestionarios">
            {!canManage && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Solo lectura"
                    description="Solo los operadores con perfil Admin pueden crear, editar, activar o eliminar cuestionarios."
                />
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16, alignItems: "center" }}>
                <Input
                    allowClear
                    prefix={<SearchOutlined style={{ color: "#BFBFBF" }} />}
                    placeholder="Buscar por título"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: 240 }}
                />
                <RangePicker
                    locale={locale}
                    format="DD/MM/YYYY"
                    value={range as any}
                    onChange={(values) => setRange(values as any)}
                    placeholder={["Desde", "Hasta"]}
                />
                <Select value={status} onChange={setStatus} style={{ width: 150 }}>
                    <Select.Option value="all">Todos</Select.Option>
                    {(Object.keys(statusLabels) as QuizStatus[]).map((s) => (
                        <Select.Option key={s} value={s}>{statusLabels[s].label}</Select.Option>
                    ))}
                </Select>
                <div style={{ flex: 1 }} />
                {canManage && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/quiz/manage/new")}>
                        Nuevo cuestionario
                    </Button>
                )}
            </div>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={filtered}
                loading={isLoading}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                scroll={{ x: 900 }}
                size="small"
            />
        </InternalPageLayout>
    );
};
export default QuizManager;
